import React from 'react';
import { Box, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { InlineMessage, SectionCard } from '../ui';
import type { LightspeedSettingsSectionProps } from './lightspeedSettingsRegistry';

const T = 'integrations.lightspeedConfig.runSummary';

type Counts = Record<string, number> | undefined;

const entriesOf = (counts: Counts): Array<[string, number]> =>
  Object.entries(counts || {})
    .filter(([, n]) => typeof n === 'number' && n > 0)
    .sort((a, b) => b[1] - a[1]);

const formatWhen = (value: unknown): string => {
  if (!value) return '';
  const record = value as Record<string, any>;
  const seconds = typeof record.seconds === 'number' ? record.seconds : typeof record._seconds === 'number' ? record._seconds : null;
  const date = seconds != null ? new Date(seconds * 1000) : new Date(String(value));
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
};

/** Why products were skipped in the last Lightspeed run (flag `lightspeed_sync_run_summary`). */
const LightspeedRunSummarySection: React.FC<LightspeedSettingsSectionProps> = ({ integration }) => {
  const { t } = useAppTranslation();
  const summary = integration.lastSyncSummary;
  const skips = entriesOf(summary?.skipReasons);
  const warnings = entriesOf(summary?.warningCodes);
  const when = formatWhen(summary?.completedAt);
  const photoLimit = summary?.photoLimit && summary.photoLimit.products > 0 ? summary.photoLimit : null;

  // An unknown code falls back to the catch-all label rather than showing a raw key.
  const label = (group: 'skip' | 'warning', code: string) =>
    t(`${T}.${group}.${code}`, { defaultValue: t(`${T}.${group}.other`) });

  const list = (title: string, group: 'skip' | 'warning', rows: Array<[string, number]>) => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>{title}</Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {rows.map(([code, count]) => (
          <li key={code}>
            <Typography variant="body2" component="span">
              {label(group, code)}: {count}
            </Typography>
          </li>
        ))}
      </Box>
    </Box>
  );

  return (
    <SectionCard title={t(`${T}.title`)} subtitle={t(`${T}.subtitle`)} dense>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {!summary ? (
          <InlineMessage severity="info">{t(`${T}.noRun`)}</InlineMessage>
        ) : (
          <>
            {when && <Typography variant="caption" color="text.secondary">{t(`${T}.completed`, { date: when })}</Typography>}
            {skips.length === 0 && warnings.length === 0 && !photoLimit && (
              <Typography variant="body2" color="text.secondary">{t(`${T}.empty`)}</Typography>
            )}
            {skips.length > 0 && list(t(`${T}.skippedTitle`), 'skip', skips)}
            {warnings.length > 0 && list(t(`${T}.warningsTitle`), 'warning', warnings)}
            {photoLimit && (
              <InlineMessage severity="info">
                {t(`${T}.photoLimit`, { products: photoLimit.products, photos: photoLimit.photosLeftOut })}
              </InlineMessage>
            )}
          </>
        )}
      </Box>
    </SectionCard>
  );
};

export default LightspeedRunSummarySection;
